import type {
  AutomationEnvironment,
  AutomationProjectDto,
  AutomationRunConfig,
  AutomationSegment,
  AutomationTeamDto,
  AutomationTestType,
} from '@tms/shared';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { Boxes, Play, Server } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../../components/layout/PageHeader';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  SelectMenu,
  SelectMenuItem,
  SkeletonRows,
} from '../../../components/ui';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { useAutomationAccess, useAutomationTeams } from '../api';
import {
  useAutomationCan,
  useClasses,
  useStartRun,
  useSuites,
  useTestData,
  useTestPlan,
} from '../hooks';
import { ProjectNotice } from '../ui';
import { CommandPreview } from './CommandPreview';
import { useRunsErrorText } from './format';
import {
  buildGradleCommand,
  propsFromTestData,
  rawTag,
  resolveGradleTask,
  segmentOfClass,
  SEGMENTS,
  sourceProjectOf,
  tagForSuite,
} from './gradle';
import { RunPreflight } from './RunPreflight';

/**
 * Starting a run (TestRun src/pages/CreateRunPage.tsx): what to run — all,
 * a suite or classes, filtered by domain tags and IE / LLP — where and how,
 * the command it makes, and for a project with a connected repository the
 * pre-flight review of accounts before the start.
 */

// For now runs go to STAGE only. Another environment added here turns the field into a choice.
const ENVS: AutomationEnvironment[] = ['STAGE'];
const BROWSERS = ['Chrome', 'Firefox', 'Safari', 'Edge'];
const DEVICES = ['Desktop', 'Mobile', 'Tablet'];

type RunType = NonNullable<AutomationRunConfig['type']>;

/**
 * What a link may preset besides the project: `suite`, `class` (repeated),
 * `tag` (repeated) and `segment` — as TestRun's suite and class tabs linked.
 */
interface RunPresets {
  suite: string;
  classes: string[];
  tags: string[];
  segment: AutomationSegment | '';
}

const useRunPresets = (): RunPresets => {
  // presets from the project page (the route declares suite, class, tag, segment)
  const search = useRouterState({ select: (state) => state.location.search }) as Record<
    string,
    unknown
  >;
  return useMemo(() => {
    const list = (value: unknown): string[] =>
      Array.isArray(value)
        ? value.map(String).filter(Boolean)
        : value === undefined || value === null || value === ''
          ? []
          : [String(value)];
    const segment = search.segment === 'IP' || search.segment === 'TOO' ? search.segment : '';
    return {
      suite: list(search.suite)[0] ?? '',
      classes: list(search.class),
      tags: list(search.tag),
      segment,
    };
  }, [search]);
};

export function CreateRunPage({ projectId }: { projectId: string }) {
  const { t } = useT();
  const navigate = useNavigate();
  const teams = useAutomationTeams();
  const can = useAutomationCan();
  // the role comes with TestRun's access; without it nobody's role is known and Run stays off
  const access = useAutomationAccess();
  const errorText = useRunsErrorText();
  const presets = useRunPresets();

  const team = teams.data?.find((tm) => tm.projects.some((p) => p.id === projectId));
  const project = team?.projects.find((p) => p.id === projectId);

  const goTo = (id: string) =>
    void navigate({ to: '/automation/runs/new', search: { project: id }, replace: true });

  // no project in the link, or one that is gone — the first project there is
  useEffect(() => {
    if (project || !teams.data) return;
    const first = teams.data.find((tm) => tm.projects.length)?.projects[0];
    if (first)
      void navigate({ to: '/automation/runs/new', search: { project: first.id }, replace: true });
  }, [project, teams.data, navigate]);

  const header = (
    <PageHeader
      title={t('automation.runs.new.title')}
      description={t('automation.runs.new.subtitle')}
    />
  );

  if (teams.error) {
    return (
      <>
        {header}
        <Card>
          <ErrorState error={teams.error} onRetry={() => void teams.refetch()} />
        </Card>
      </>
    );
  }
  if (!teams.data) {
    return (
      <>
        {header}
        <SkeletonRows rows={4} className="p-0" />
      </>
    );
  }
  if (!teams.data.some((tm) => tm.projects.length)) {
    return (
      <>
        {header}
        <Card>
          <EmptyState title={t('automation.runs.new.noProjects')} />
        </Card>
      </>
    );
  }

  return (
    <>
      {header}
      {/* Without this a Viewer filled in the whole form and met the server's refusal at the last step */}
      {can.role !== undefined && !can.run ? (
        <ProjectNotice
          title={t('automation.runs.new.noRunTitle')}
          note={t('automation.runs.new.noRunNote', { role: can.role })}
        />
      ) : null}
      {access.error ? (
        <ProjectNotice
          title={t('automation.runs.new.noRunTitle')}
          note={t('automation.runs.new.noAccess', { error: errorText(access.error) })}
        />
      ) : null}
      {team && project ? (
        <RunForm
          // another project starts from a clean form, as TestRun reset it
          key={project.id}
          project={project}
          presets={presets}
          canRun={can.run}
          selectors={
            <ProjectSelectors
              teams={teams.data}
              team={team}
              project={project}
              onTeam={(next) => {
                const first = next.projects[0];
                if (first) goTo(first.id);
              }}
              onProject={goTo}
            />
          }
        />
      ) : (
        <SkeletonRows rows={4} className="p-0" />
      )}
    </>
  );
}

const ProjectSelectors = ({
  teams,
  team,
  project,
  onTeam,
  onProject,
}: {
  teams: AutomationTeamDto[];
  team: AutomationTeamDto;
  project: AutomationProjectDto;
  onTeam: (team: AutomationTeamDto) => void;
  onProject: (projectId: string) => void;
}) => {
  const { t } = useT();
  const teamId = useId();
  const projectId = useId();
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <FormField id={teamId} label={t('automation.runs.new.team')}>
        <SelectMenu
          id={teamId}
          value={team.id}
          onValueChange={(id) => {
            const next = teams.find((tm) => tm.id === id);
            if (next) onTeam(next);
          }}
        >
          {teams
            .filter((tm) => tm.projects.length)
            .map((tm) => (
              <SelectMenuItem key={tm.id} value={tm.id}>
                {tm.name}
              </SelectMenuItem>
            ))}
        </SelectMenu>
      </FormField>
      <FormField id={projectId} label={t('automation.runs.new.project')}>
        <SelectMenu id={projectId} value={project.id} onValueChange={onProject}>
          {team.projects.map((p) => (
            <SelectMenuItem key={p.id} value={p.id}>
              {p.name} · {p.testType}
            </SelectMenuItem>
          ))}
        </SelectMenu>
      </FormField>
    </div>
  );
};

/** A label over a control, with an optional hint under it. */
const FormField = ({
  id,
  label,
  hint,
  children,
}: {
  id?: string;
  label: ReactNode;
  hint?: ReactNode;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    {id ? (
      <label htmlFor={id} className="text-[13px] font-medium text-ink">
        {label}
      </label>
    ) : (
      <p className="text-[13px] font-medium text-ink">{label}</p>
    )}
    {children}
    {hint ? <p className="text-[12px] text-ink-subtle">{hint}</p> : null}
  </div>
);

/** One choice of a few: a row of toggle buttons. */
const Segmented = <T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string; title?: string }>;
  onChange: (value: T) => void;
}) => (
  <div role="group" aria-label={label} className="flex flex-wrap gap-2">
    {options.map((option) => {
      const active = option.value === value;
      return (
        <button
          key={option.value || 'all'}
          type="button"
          aria-pressed={active}
          title={option.title}
          onClick={() => onChange(option.value)}
          className={cn(
            'h-8 min-w-[100px] flex-1 rounded-md border px-3 text-[13px] font-medium transition-colors',
            active
              ? 'border-accent bg-accent-soft text-accent-fg'
              : 'border-border bg-surface text-ink-muted hover:bg-sunken hover:text-ink',
          )}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

const Code = ({ children }: { children: ReactNode }) => (
  <code className="rounded bg-sunken px-1 font-mono text-[11.5px] text-ink">{children}</code>
);

const RunForm = ({
  project,
  presets,
  canRun,
  selectors,
}: {
  project: AutomationProjectDto;
  presets: RunPresets;
  canRun: boolean;
  selectors: ReactNode;
}) => {
  const { t } = useT();
  const navigate = useNavigate();
  const errorText = useRunsErrorText();
  const start = useStartRun(project.id);
  const suiteFieldId = useId();
  const envId = useId();
  const browserId = useId();
  const deviceId = useId();

  const [type, setType] = useState<RunType>(
    presets.suite ? 'suite' : presets.classes.length ? 'classes' : 'all',
  );
  const [suite, setSuite] = useState(presets.suite);
  const [selClasses, setSelClasses] = useState<string[]>(presets.classes);
  const [testType, setTestType] = useState<AutomationTestType>(project.testType);
  const [env, setEnv] = useState<AutomationEnvironment>(ENVS[0] ?? 'STAGE');
  const [browser, setBrowser] = useState(BROWSERS[0] ?? 'Chrome');
  const [device, setDevice] = useState(DEVICES[0] ?? 'Desktop');
  const [domainTags, setDomainTags] = useState<string[]>(presets.tags);
  // IE / LLP by @Tag("IP") / @Tag("TOO"): '' — both
  const [segment, setSegment] = useState<AutomationSegment | ''>(presets.segment);
  const [preflightOpen, setPreflightOpen] = useState(false);

  const plan = useTestPlan(project.id);
  const suites = useSuites(project.id);
  const classes = useClasses(project.id);
  const testData = useTestData(project.id);

  const source = useMemo(() => sourceProjectOf(project.sourceProject), [project.sourceProject]);
  /** the scenario's name, as in the pre-flight: @DisplayName; a testdata comment is a note to the data, not a name */
  const classTitle = (name: string): string | null => {
    const c = plan.data?.classes.find((x) => x.class === name);
    return c?.displayName ?? c?.comment ?? null;
  };

  // account keys are reviewed per class in the pre-flight and never go out globally
  const dProps = useMemo(() => propsFromTestData(testData.data ?? []), [testData.data]);
  const gradleTask = source ? resolveGradleTask(env, source.envTasks) : 'test';
  // a repository suite is a domain tag shown through prettyTag; classes carry the tag itself
  const suiteTag = suite && source ? tagForSuite(suite, source.domainTags) : suite;
  // the server's order: explicit tags win over the suite, which goes as its tag
  const runTags = useMemo(
    () => (domainTags.length ? domainTags : type === 'suite' && suite ? [rawTag(suite)] : []),
    [domainTags, type, suite],
  );
  const command = useMemo(
    () =>
      buildGradleCommand({
        task: gradleTask,
        domainTags: runTags,
        segment: segment || null,
        classes: type === 'classes' ? selClasses : null,
        props: dProps,
      }),
    [gradleTask, runTags, segment, type, selClasses, dProps],
  );

  // the classes that really go into the run — with the tag and segment filters
  const selectedClasses = useMemo(() => {
    let list = classes.data ?? [];
    if (type === 'suite' && suite)
      list = list.filter((c) => c.suite === suiteTag || c.suite === suite);
    else if (type === 'classes') list = list.filter((c) => selClasses.includes(c.name));
    if (domainTags.length)
      list = list.filter((c) => c.tags.some((tag) => domainTags.includes(tag)));
    if (segment) list = list.filter((c) => segmentOfClass(c) === segment);
    return list;
  }, [classes.data, type, suite, suiteTag, selClasses, domainTags, segment]);

  // the project's segments: from the scan, or by the classes' tags for older scans
  const segments = useMemo(() => {
    if (source?.segments) return source.segments;
    const count = new Map<AutomationSegment, number>();
    for (const c of classes.data ?? []) {
      const s = segmentOfClass(c);
      if (s) count.set(s, (count.get(s) ?? 0) + 1);
    }
    return SEGMENTS.filter((s) => count.has(s)).map((s) => ({
      segment: s,
      count: count.get(s) ?? 0,
    }));
  }, [source, classes.data]);

  const estimate = useMemo(() => {
    if (classes.data?.length) return selectedClasses.reduce((sum, c) => sum + c.testCount, 0);
    // no repository — no class catalogue, the rough estimates stay
    if (type === 'all') return project.testCount;
    if (type === 'suite') return suites.data?.find((s) => s.name === suite)?.testCount ?? 0;
    return 0;
  }, [classes.data, selectedClasses, type, project.testCount, suites.data, suite]);

  const valid =
    type === 'all' ||
    (type === 'suite' && Boolean(suite)) ||
    (type === 'classes' && selClasses.length > 0);

  const doLaunch = async (config: AutomationRunConfig) => {
    try {
      const { run, marksError } = await start.mutateAsync(config);
      toast.success(t('automation.runs.new.created', { id: run.id }));
      if (marksError !== null) {
        // the run has started; only TestRun's marks of the pool accounts are missing
        toast.warning(
          t('automation.runs.new.marksFailed', {
            error: marksError || t('automation.unreachable'),
          }),
        );
      }
      void navigate({
        to: '/automation/runs/$runId',
        params: { runId: run.pipelineId },
        search: { project: project.id },
      });
    } catch (error) {
      toast.error(errorText(error));
    }
  };

  const submit = () => {
    // a project with a connected repository goes through the pre-flight review
    if (source) {
      setPreflightOpen(true);
      return;
    }
    void doLaunch({
      type,
      ...(type === 'suite' ? { suite } : {}),
      ...(type === 'classes' ? { classes: selClasses } : {}),
      testType,
      environment: env,
      ...(testType === 'UI' ? { browser, device } : {}),
      gradleTask,
      ...(domainTags.length ? { domainTags } : {}),
      ...(segment ? { segment } : {}),
    });
  };

  const toggleClass = (name: string) =>
    setSelClasses((prev) =>
      prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name],
    );
  const toggleTag = (tag: string) =>
    setDomainTags((prev) => (prev.includes(tag) ? prev.filter((x) => x !== tag) : [...prev, tag]));

  const segmentLabel = (s: AutomationSegment) => t(`automation.runs.segment.${s}`);
  const scopeValue =
    type === 'all'
      ? t('automation.runs.new.type.all')
      : type === 'suite'
        ? suite || '—'
        : t('automation.runs.new.summaryClasses', { count: selClasses.length });

  return (
    <>
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <Card>
          <CardBody className="space-y-5">
            {selectors}
            <ProjectNotice note={project.note} />

            <FormField label={t('automation.runs.new.runType')}>
              <Segmented
                label={t('automation.runs.new.runType')}
                value={type}
                onChange={setType}
                options={[
                  { value: 'all', label: t('automation.runs.new.type.all') },
                  { value: 'suite', label: t('automation.runs.new.type.suite') },
                  { value: 'classes', label: t('automation.runs.new.type.classes') },
                ]}
              />
            </FormField>

            {type === 'suite' ? (
              <FormField id={suiteFieldId} label={t('automation.runs.new.pickSuite')}>
                <SelectMenu
                  id={suiteFieldId}
                  value={suite}
                  onValueChange={setSuite}
                  placeholder={t('automation.runs.new.suitePlaceholder')}
                >
                  {(suites.data ?? []).map((s) => (
                    <SelectMenuItem key={s.id} value={s.name}>
                      {t('automation.runs.new.suiteOption', { name: s.name, count: s.testCount })}
                    </SelectMenuItem>
                  ))}
                </SelectMenu>
              </FormField>
            ) : null}

            {type === 'classes' ? (
              <FormField label={t('automation.runs.new.pickClasses', { count: selClasses.length })}>
                <ul className="max-h-[260px] overflow-auto rounded-lg border border-border p-1">
                  {(classes.data ?? [])
                    .filter((c) => !segment || segmentOfClass(c) === segment)
                    .map((c) => {
                      const on = selClasses.includes(c.name);
                      const title = classTitle(c.name);
                      return (
                        <li key={c.id}>
                          <label
                            className={cn(
                              'flex cursor-pointer items-center justify-between gap-3 rounded-md px-2.5 py-2',
                              on ? 'bg-accent-soft' : 'hover:bg-sunken',
                            )}
                          >
                            <span className="flex min-w-0 items-center gap-2.5">
                              <input
                                type="checkbox"
                                checked={on}
                                onChange={() => toggleClass(c.name)}
                                className="size-4 shrink-0 accent-accent"
                              />
                              {title ? (
                                <span className="min-w-0">
                                  <span className="block text-[13px] font-medium text-ink">
                                    {title}
                                  </span>
                                  <span className="block font-mono text-[11px] text-ink-subtle">
                                    {c.name}
                                  </span>
                                </span>
                              ) : (
                                <span className="font-mono text-[13px] font-medium text-ink">
                                  {c.name}
                                </span>
                              )}
                            </span>
                            <span className="shrink-0 text-[12px] text-ink-subtle">
                              {c.suite} · {c.testCount}
                            </span>
                          </label>
                        </li>
                      );
                    })}
                </ul>
              </FormField>
            ) : null}

            {source ? (
              <FormField
                label={
                  <>
                    {t('automation.runs.new.domains')}
                    {domainTags.length > 0
                      ? ` · ${t('automation.runs.new.domainsChosen', { count: domainTags.length })}`
                      : ''}
                  </>
                }
                hint={
                  domainTags.length > 1 ? (
                    <>
                      {t('automation.runs.new.domainsHintMany')}{' '}
                      <Code>-Dgroups=&quot;({domainTags.join(' | ')})&quot;</Code>{' '}
                      {t('automation.runs.new.domainsHintAny')}
                    </>
                  ) : (
                    <>
                      {t('automation.runs.new.domainsHint')} <Code>-Dgroups=…</Code>.{' '}
                      {t('automation.runs.new.domainsHintSeveral')}
                    </>
                  )
                }
              >
                <div className="flex flex-wrap gap-2">
                  {source.domainTags.map((d) => {
                    const on = domainTags.includes(d.tag);
                    return (
                      <button
                        key={d.tag}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleTag(d.tag)}
                        className={cn(
                          'inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[12px] transition-colors',
                          on
                            ? 'border-accent bg-accent-soft text-accent-fg'
                            : 'border-border bg-surface text-ink-muted hover:bg-sunken',
                        )}
                      >
                        <span className="font-mono">{d.tag}</span>
                        <span className="text-ink-subtle">{d.count}</span>
                      </button>
                    );
                  })}
                  {domainTags.length > 0 ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7"
                      onClick={() => setDomainTags([])}
                    >
                      {t('automation.runs.new.domainsReset')}
                    </Button>
                  ) : null}
                </div>
              </FormField>
            ) : null}

            {source && segments.length > 0 ? (
              <FormField
                label={t('automation.runs.new.segment')}
                hint={
                  <>
                    {t('automation.runs.new.segmentHint')} <Code>@Tag(&quot;IP&quot;)</Code> /{' '}
                    <Code>@Tag(&quot;TOO&quot;)</Code>
                    {segment ? (
                      <>
                        {' → '}
                        {t('automation.runs.new.segmentHintOn')} <Code>&amp; {segment}</Code>{' '}
                        {t('automation.runs.new.segmentHintTo')} <Code>-Dgroups</Code>
                      </>
                    ) : (
                      ` — ${t('automation.runs.new.segmentHintOff')}`
                    )}
                  </>
                }
              >
                <Segmented<AutomationSegment | ''>
                  label={t('automation.runs.new.segment')}
                  value={segment}
                  onChange={setSegment}
                  options={[
                    { value: '', label: t('automation.runs.new.segmentAll') },
                    ...segments.map((s) => ({
                      value: s.segment,
                      label: `${segmentLabel(s.segment)} · ${s.count}`,
                    })),
                  ]}
                />
              </FormField>
            ) : null}

            <hr className="border-border" />

            <FormField
              label={t('automation.runs.new.testType')}
              hint={t(`automation.runs.new.testTypeNote.${testType}`)}
            >
              <Segmented<AutomationTestType>
                label={t('automation.runs.new.testType')}
                value={testType}
                onChange={setTestType}
                options={(['API', 'UI'] as const).map((value) => ({
                  value,
                  label: t(`automation.runs.new.testType.${value}`),
                  title: t(`automation.runs.new.testTypeHint.${value}`),
                }))}
              />
            </FormField>

            <div className="grid gap-4 sm:grid-cols-2">
              {ENVS.length > 1 ? (
                <FormField id={envId} label={t('automation.runs.new.environment')}>
                  <SelectMenu
                    id={envId}
                    value={env}
                    onValueChange={(value) => setEnv(value as AutomationEnvironment)}
                  >
                    {ENVS.map((e) => (
                      <SelectMenuItem key={e} value={e}>
                        {e}
                      </SelectMenuItem>
                    ))}
                  </SelectMenu>
                </FormField>
              ) : (
                <FormField
                  label={t('automation.runs.new.environment')}
                  hint={t('automation.runs.new.envOnly')}
                >
                  <p className="flex h-9 items-center rounded-md border border-border bg-sunken px-2.5 text-ink-muted">
                    {env}
                  </p>
                </FormField>
              )}
              {testType === 'UI' ? (
                <>
                  <FormField id={browserId} label={t('automation.runs.new.browser')}>
                    <SelectMenu id={browserId} value={browser} onValueChange={setBrowser}>
                      {BROWSERS.map((b) => (
                        <SelectMenuItem key={b} value={b}>
                          {b}
                        </SelectMenuItem>
                      ))}
                    </SelectMenu>
                  </FormField>
                  <FormField id={deviceId} label={t('automation.runs.new.device')}>
                    <SelectMenu id={deviceId} value={device} onValueChange={setDevice}>
                      {DEVICES.map((d) => (
                        <SelectMenuItem key={d} value={d}>
                          {d}
                        </SelectMenuItem>
                      ))}
                    </SelectMenu>
                  </FormField>
                </>
              ) : null}
            </div>
          </CardBody>
        </Card>

        <Card className="lg:sticky lg:top-4">
          <CardHeader title={t('automation.runs.new.summary')} />
          <CardBody className="space-y-3">
            <dl className="space-y-2.5">
              <SummaryRow
                icon={<Boxes className="size-3.5" />}
                label={t('automation.runs.new.summaryTests')}
                value={String(estimate)}
              />
              <SummaryRow
                icon={<Server className="size-3.5" />}
                label={t('automation.runs.new.testType')}
                value={testType}
              />
              <SummaryRow
                icon={<Server className="size-3.5" />}
                label={t('automation.runs.new.environment')}
                value={env}
              />
              {testType === 'UI' ? (
                <SummaryRow
                  icon={<Play className="size-3.5" />}
                  label={t('automation.runs.new.browser')}
                  value={`${browser} · ${device}`}
                />
              ) : null}
              <SummaryRow
                icon={<Play className="size-3.5" />}
                label={t('automation.runs.new.summaryScope')}
                value={scopeValue}
              />
            </dl>
            <hr className="border-border" />
            <Button
              variant="primary"
              className="w-full"
              disabled={!canRun || !valid}
              loading={start.isPending}
              onClick={submit}
            >
              {start.isPending ? null : <Play className="size-4" aria-hidden="true" />}
              {start.isPending
                ? t('automation.runs.new.creating')
                : source
                  ? t('automation.runs.new.launchChecked')
                  : t('automation.runs.new.launch')}
            </Button>
            <p className="text-center text-[12px] text-ink-subtle">
              {t('automation.runs.new.launchNote')}
            </p>
          </CardBody>
        </Card>
      </div>

      <CommandPreview
        command={command}
        dProps={dProps}
        fromRepo={Boolean(source)}
        gradleTask={gradleTask}
      />

      <RunPreflight
        open={preflightOpen}
        onClose={() => setPreflightOpen(false)}
        projectId={project.id}
        base={{
          gradleTask,
          type,
          ...(type === 'suite' ? { suite } : {}),
          ...(type === 'classes' ? { classes: selClasses } : {}),
          domainTags,
          ...(segment ? { segment } : {}),
          testType,
          environment: env,
          ...(testType === 'UI' ? { browser, device } : {}),
          globalProps: dProps,
        }}
        onLaunch={(config) => {
          setPreflightOpen(false);
          void doLaunch(config);
        }}
        pending={start.isPending}
      />
    </>
  );
};

const SummaryRow = ({ icon, label, value }: { icon: ReactNode; label: string; value: string }) => (
  <div className="flex items-center justify-between gap-3">
    <dt className="flex items-center gap-2 text-[13px] text-ink-muted">
      <span aria-hidden="true">{icon}</span>
      {label}
    </dt>
    <dd className="text-right text-[14px] font-semibold text-ink">{value}</dd>
  </div>
);
